import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import logger from 'ws-scripts/modules/logger';

// The module loader debounces by 100ms and may still have to download
// validator.js, so give it a generous window before reporting a miss.
const VALIDATOR_INIT_TIMEOUT_MS = 5000;

const ContactFormFragment = ({ fragmentId, fragmentUrl }) => {
	const formFragmentRef = useRef(null);

	useEffect(() => {
		let validatorInitTimer = null;
		let stopWatchingValidator = null;

		// loading animation
		$(formFragmentRef.current).not('.ajax-load-complete')
			.before(`<span class="loading-ajax-container loading">
						<i class="ddc-icon ddc-icon-specs-gear1 ddc-icon-size-xlarge"></i> Loading...
					 </span>`);

		// form fragment
		$(formFragmentRef.current)
			.removeClass('hide')
			.load(encodeURI(fragmentUrl), (response, status) => {
				$(formFragmentRef.current)
					.css('visibility', 'visible')
					.siblings('.loading')
					.remove()
					.find('.ddc-content');

				// jQuery attaches this callback with `.always()`, so it also
				// runs when the request failed and no markup was injected.
				if (status !== 'success' && status !== 'notmodified') {
					return;
				}

				// Modal may have closed before this resolved; a null ref makes
				// `onlyRefreshModulesWithin` falsy, and default.js then loads
				// every module page-wide.
				if (!formFragmentRef.current?.isConnected) {
					return;
				}

				// validator.js publishes `validatorInitialized` per form once it
				// has run. If that never arrives the consent fields were not
				// populated and the lead will be rejected downstream, so say so
				// rather than failing silently.
				// Hold on to the unsubscribe we paired with this subscription
				// rather than resolving `$` again later.
				const { unsubscribe } = $;
				const onValidatorInit = (e, $form) => {
					if (formFragmentRef.current?.contains($form?.[0])) {
						clearTimeout(validatorInitTimer);
						stopWatchingValidator();
					}
				};

				stopWatchingValidator = () =>
					unsubscribe('validatorInitialized', onValidatorInit);

				$.subscribe('validatorInitialized', onValidatorInit);

				validatorInitTimer = setTimeout(() => {
					stopWatchingValidator();
					logger.warn(
						'[ContactFormFragment] validator did not initialise the contact form; consent fields were not populated'
					);
				}, VALIDATOR_INIT_TIMEOUT_MS);

				// The DDC module loader only scans the DOM when it is triggered.
				// This fragment is injected after page load, so without this the
				// validator module never initialises the form and the consent
				// fields keep their server defaults. `onlyRefreshModulesWithin`
				// initialises every module the fragment needs (validator,
				// noSpam, dynamicField, placeholder) while leaving everything
				// outside the modal untouched.
				$(document).trigger('modulesRefresh', {
					onlyRefreshModulesWithin: formFragmentRef.current
				});
			});

		return () => {
			clearTimeout(validatorInitTimer);
			stopWatchingValidator?.();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			className="hide ajax-form-element"
			data-fragment-id={fragmentId}
			data-fragment-url={fragmentUrl}
			ref={formFragmentRef}
		/>
	);
};

export default ContactFormFragment;

ContactFormFragment.propTypes = {
	fragmentId: PropTypes.string.isRequired,
	fragmentUrl: PropTypes.string.isRequired
};
